import { Stack, useBreakpointValue } from "@chakra-ui/react";
import { SectionContentLayout } from "../../../components/utils";
import type { DealFinderTicker } from "../../../../../api/tickers/models/DealFinderTicker";
import { useCollectionKey, useDealFinderTickers } from "../../../../../hooks";
import React from "react";
import SectionInvestorReadiness from "../../CommonComponents/Sections/SectionContents/SectionInvestorReadiness";
import SectionCompanyNews from "../../CommonComponents/Sections/SectionContents/SectionCompanyNews";
import SectionCorporateCommunications from "../../CommonComponents/Sections/SectionContents/SectionCorporateCommunications";
import SectionNewUpdate from "../../CommonComponents/Sections/SectionContents/SectionNewUpdate";
import SectionCompanyBanner from "../../CommonComponents/Sections/SectionContents/SectionCompanyBanner";
import SectionCompanyVideo from "../../CommonComponents/Sections/SectionContents/SectionCompanyVideo";
import SectionCompanyProfile from "../../CommonComponents/Sections/SectionContents/SectionCompanyProfile";
import SectionBusinessDescription from "../../CommonComponents/Sections/SectionContents/SectionBusinessDescription";

interface Props {
  collectionId: string;
  isLoading?: boolean;
}

export const CompanyDetailViewCompanyTab: React.FC<Props> = ({ collectionId, isLoading = false }) => {
  const [tickerData, setTickerData] = React.useState<DealFinderTicker | null>(null);

  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });

  const { getDealFinderTickers } = useDealFinderTickers();

  const ticker = useCollectionKey(collectionId, "ticker");
  const exchange = useCollectionKey(collectionId, "stockExchange");

  // Fetch ticker data
  React.useEffect(() => {
    const fetchTickerData = async () => {
      if (ticker && exchange) {
        try {
          const data = await getDealFinderTickers({
            symbolWithExchange: `${exchange}:${ticker}`,
          });
          setTickerData(data.length > 0 ? data[0] : null);
        } catch (error) {
          console.error("Error fetching ticker data:", error);
          setTickerData(null);
        }
      }
    };

    fetchTickerData();
  }, [ticker, exchange, getDealFinderTickers]);

  // Mock investment summary data (would come from ticker data in real implementation)
  const corporateCommunicationsData = React.useMemo(() => {
    if (tickerData) {
      return [
        { title: "Allumiqs Corporation - Q4 2024", status: "Removed", createdDate: 1727913600 },
        { title: "Allumiqs Corporation - Q3 2024", status: "Draft", createdDate: 1719964800 },
        { title: "Allumiqs Corporation - Q2 2024", status: "Published", createdDate: 1712102400 },
        { title: "Allumiqs Corporation - Q1 2024", status: "Published", createdDate: 1704240000 },
        { title: "Allumiqs Corporation - Q4 2023", status: "Published", createdDate: 1696291200 },
      ];
    }
    // Mock data when no real data available
    return [
      { title: "Allumiqs Corporation - Q4 2024", status: "Removed", createdDate: 1727913600 },
      { title: "Allumiqs Corporation - Q3 2024", status: "Draft", createdDate: 1719964800 },
      { title: "Allumiqs Corporation - Q2 2024", status: "Published", createdDate: 1712102400 },
      { title: "Allumiqs Corporation - Q1 2024", status: "Published", createdDate: 1704240000 },
      { title: "Allumiqs Corporation - Q4 2023", status: "Published", createdDate: 1696291200 },
    ];
  }, [tickerData]);

  const companyProfileData = React.useMemo(() => {
    if (tickerData) {
      return {
        legalName: "Allumiqs Corporation",
        companyName: "Allumiqs",
        industry: "Lifesciences",
        industryDescription: "",
        website: "https://allumiqs.com",
      };
    }
    // Mock data when no real data available
    return {
      legalName: "Allumiqs Corporation",
      companyName: "Allumiqs",
      industry: "Lifesciences",
      industryDescription: "",
      website: "https://allumiqs.com",
    };
  }, [tickerData]);

  const companyNewsData = React.useMemo(() => {
    if (tickerData) {
      return [
        { title: "Mass Spectrometry: Unlocking Deeper Insights for Geneticists", previewImageURL: "", publishDate: 1735603200 },
        {
          title: "Allumiqs Welcomes David Graham as Head of Scientific Business Development",
          previewImageURL: "",
          publishDate: 1735603200,
        },
        { title: "Allumiqs Announces Derek Gooderham's Appointment to Board of Directors", previewImageURL: "", publishDate: 1735603200 },
        {
          title: "Allumiqs Expands Metabolomics Solutions as Part of the Biocrates Certified Laboratory Program",
          previewImageURL: "",
          publishDate: 1735603200,
        },
      ];
    }
    // Mock data when no real data available
    return [
      {
        title: "Allumiqs Expands Metabolomics Solutions as Part of the Biocrates Certified Laboratory Program",
        previewImageURL:
          "https://backendlessappcontent.com/0C7079AC-2AA6-4757-FF6B-0389C13B6900/5FD59993-C2FF-43C0-8D2F-7B985CA52D5D/files/public/article4.jpg",
        publishDate: 1524096000,
      },
      {
        title: "Mass Spectrometry: Unlocking Deeper Insights for Geneticists",
        previewImageURL:
          "https://backendlessappcontent.com/0C7079AC-2AA6-4757-FF6B-0389C13B6900/5FD59993-C2FF-43C0-8D2F-7B985CA52D5D/files/public/article1.jpg",
        publishDate: 1735603200,
      },
      {
        title: "Allumiqs Announces Derek Gooderham's Appointment to Board of Directors",
        previewImageURL:
          "https://backendlessappcontent.com/0C7079AC-2AA6-4757-FF6B-0389C13B6900/5FD59993-C2FF-43C0-8D2F-7B985CA52D5D/files/public/article3.jpg",
        publishDate: 1643587200,
      },
      {
        title: "Allumiqs Welcomes David Graham as Head of Scientific Business Development",
        previewImageURL:
          "https://backendlessappcontent.com/0C7079AC-2AA6-4757-FF6B-0389C13B6900/5FD59993-C2FF-43C0-8D2F-7B985CA52D5D/files/public/article2.jpg",
        publishDate: 1678060800,
      },
    ];
  }, [tickerData]);

  // Mock data for sections that can't be populated with real data
  const mockInvestorReadinessScore = 34;

  console.log("Is page loading: ", isLoading);

  return (
    <SectionContentLayout useGrid className="ch-company-dashboard-sections">
      {/* Left Column */}
      <Stack spacing={isMobile ? ".5rem" : "1rem"}>
        <SectionInvestorReadiness score={mockInvestorReadinessScore} />
        <SectionNewUpdate companyLogoURL={""} />
        <SectionCorporateCommunications updates={corporateCommunicationsData} />
        <SectionCompanyNews articles={companyNewsData} />
      </Stack>

      {/* Right Column */}
      <Stack spacing={isMobile ? ".5rem" : "1rem"}>
        <SectionCompanyBanner companyBannerURL={""} />
        <SectionCompanyVideo companyVideoURL={""} />
        <SectionCompanyProfile companyProfile={companyProfileData} />
        <SectionBusinessDescription businessDescription={""} />
      </Stack>
    </SectionContentLayout>
  );
};
